import { useMutation } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, CircularProgress, makeStyles, Theme } from '@material-ui/core'
import VisibilityOffSharpIcon from '@material-ui/icons/VisibilityOffSharp'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import { SetProjectElementNodeIsDeactivatedMutation } from 'api/graphQL/mutations/projectProductFamily.mutations'
import {
    reactiveWorkspaceContents,
    setWorkspaceDTV,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    deactivatePTLabel: {
        cursor: 'pointer',
        background: 'transparent',
        padding: theme.spacing(0),
    },
    deactivatePTIcon: {
        fontSize: pxToRem(20),
        marginRight: theme.spacing(1),
    },
}))

/** Represents state and functions for DeactivatePTDropdown component */
interface DeactivatePTDropdownProps {
    /** Indicates whether the element is currently deactivated. */
    isDeactivated?: boolean
    /** The baseElementId of the node element. */
    baseElementId?: string
    /** The id of the node element. */
    id?: string
    /** The elementId of the node element. */
    elementId?: string
    /** The type of the node element. */
    elementType?: string
    /** Function to control the visibility of the action popover. */
    setIsActionPopoverDisplayed: (value: boolean) => void
    /** Function to update the list of project elements. */
    updateElementList?: (elementList: ProjectElementNode[]) => void
    /** Callback function triggered when the node is loaded. */
    onNodeLoad?: VoidFunction
}

const DeactivatePTDropdown = ({
    isDeactivated,
    baseElementId,
    id,
    elementId,
    elementType,
    setIsActionPopoverDisplayed,
    updateElementList,
    onNodeLoad,
}: DeactivatePTDropdownProps) => {
    const classes = useStyles()
    const { setSelectedElementIdentifier } = React.useContext(
        ProjectPageContext
    )
    const [
        setProjectElementNodeIsDeactivatedMutation,
        { loading: projectElementNodeDeactivationLoading },
    ] = useMutation(SetProjectElementNodeIsDeactivatedMutation, {
        fetchPolicy: 'no-cache',
        onCompleted: React.useCallback(
            (data) => {
                setSelectedElementIdentifier?.(undefined)
                setWorkspaceDTV(null)
                updateElementList?.([data?.setProjectElementNodeIsDeactivated])
                onNodeLoad?.()
                setIsActionPopoverDisplayed(false)
            },
            [baseElementId, id, elementId, elementType]
        ),
    })

    const handlePTDeactivateInOMWorkspace = React.useCallback(() => {
        const { dtv } = reactiveWorkspaceContents()
        setProjectElementNodeIsDeactivatedMutation({
            variables: {
                DeactivateProductTypeInput: {
                    documentTemplateViewId: dtv?.id,
                    nodeId: elementId,
                    isDeactivated: !isDeactivated,
                },
            },
        })
    }, [elementId, id, isDeactivated])

    const CurrentIcon = React.useMemo(() => {
        switch (true) {
            case projectElementNodeDeactivationLoading:
                return (
                    <CircularProgress
                        size={16}
                        className={classes.deactivatePTIcon}
                    />
                )
            case isDeactivated:
                return (
                    <VisibilityOutlinedIcon
                        className={classes.deactivatePTIcon}
                    />
                )
            default:
                return (
                    <VisibilityOffSharpIcon
                        className={classes.deactivatePTIcon}
                    />
                )
        }
    }, [isDeactivated, projectElementNodeDeactivationLoading])

    return (
        <li role="menuitem">
            <Button
                tabIndex={0}
                className={classes.deactivatePTLabel}
                onClick={handlePTDeactivateInOMWorkspace}
                disabled={projectElementNodeDeactivationLoading}
            >
                {CurrentIcon}
                {isDeactivated ? `Activate` : `Deactivate`} Product Type
            </Button>
        </li>
    )
}

export default DeactivatePTDropdown
